/**
 * Sticky Navigation Component
 * Fixed position navigation with smooth scroll and active section highlighting
 */

import React, { useState } from 'react';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';

const StickyNavigation = () => {
  const activeSection = useIntersectionObserver();
  const [hoveredItem, setHoveredItem] = useState(null);

  const navItems = [
    {
      id: 'study-anchor',
      label: 'Study',
      icon: '📚',
    },
    {
      id: 'gym-anchor',
      label: 'Gym',
      icon: '💪',
    },
    {
      id: 'office-anchor',
      label: 'Office',
      icon: '💼',
    },
    {
      id: 'closet-anchor',
      label: 'Closet',
      icon: '👗',
    },
  ];

  const handleClick = (e, anchor) => {
    e.preventDefault();
    const element = document.getElementById(anchor);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <nav
      className="scroll-menu fixed z-50
        right-0 top-1/2 -translate-y-1/2
        max-md:bottom-8 max-md:top-auto max-md:translate-y-0
        flex flex-col gap-4 items-end"
      aria-label="Page navigation"
    >
      {navItems.map((item) => {
        const isActive = activeSection === item.id;
        const isHovered = hoveredItem === item.id;

        return (
          <a
            key={item.id}
            href={`#${item.id}`}
            className={`scroll-menu-item
              flex items-center gap-3
              rounded-l-full
              transition-all duration-300 ease-out
              ${isActive ? 'bg-pink-400 text-white' : 'bg-white text-pink-500'}
              ${isActive || isHovered ? 'pl-4 pr-3 py-3 shadow-lg' : 'pl-3 pr-3 py-3 shadow-md'}
              hover:shadow-xl
              group
            `}
            onClick={(e) => handleClick(e, item.id)}
            onMouseEnter={() => setHoveredItem(item.id)}
            onMouseLeave={() => setHoveredItem(null)}
            aria-current={isActive ? 'true' : 'false'}
          >
            {/* Label - expands on hover (now on the left) */}
            <span
              className={`menu-label font-medium whitespace-nowrap overflow-hidden
                transition-all duration-300
                ${isActive || isHovered ? 'max-w-[100px] opacity-100' : 'max-w-0 opacity-0'}
              `}
            >
              {item.label}
            </span>

            {/* Icon (now on the right) */}
            <span className="menu-icon text-xl flex-shrink-0" role="img" aria-label={item.label}>
              {item.icon}
            </span>
          </a>
        );
      })}

      {/* Shop link - half obround */}
      <a
        href="/shop.html"
        className={`scroll-menu-item
          flex items-center gap-3
          rounded-l-full
          transition-all duration-300 ease-out
          bg-white text-pink-500
          ${hoveredItem === 'shop' ? 'pl-4 pr-3 py-3 shadow-lg' : 'pl-3 pr-3 py-3 shadow-md'}
          hover:shadow-xl
          group
          mt-4
        `}
        onMouseEnter={() => setHoveredItem('shop')}
        onMouseLeave={() => setHoveredItem(null)}
        title="Shop my favorites"
      >
        {/* Label - expands on hover */}
        <span
          className={`menu-label font-medium whitespace-nowrap overflow-hidden
            transition-all duration-300
            ${hoveredItem === 'shop' ? 'max-w-[100px] opacity-100' : 'max-w-0 opacity-0'}
          `}
        >
          Shop
        </span>

        {/* Icon */}
        <span className="menu-icon text-xl flex-shrink-0" role="img" aria-label="Shop">
          🛒
        </span>
      </a>

      {/* Contact link - stays circular */}
      <a
        href="/contact"
        className="scroll-menu-item
          flex items-center justify-center
          w-12 h-12
          rounded-full
          bg-white text-purple-400
          hover:bg-purple-400 hover:text-white
          transition-all duration-300
          shadow-md hover:shadow-lg
          mt-4
          mr-0
        "
        title="Contact"
      >
        <span className="text-xl" role="img" aria-label="Contact">
          ✉️
        </span>
      </a>
    </nav>
  );
};

export default StickyNavigation;
